import { ButtonType, GhostButton } from '@ardoq/button';
import { ExportConfigParams } from '@ardoq/view-settings';
import { MouseEventHandler, useMemo } from 'react';
import {
  ButtonWithDropdown,
  DropdownItem,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { IconName, IconSize } from '@ardoq/icons';
import { Features, hasFeature } from '@ardoq/features';
import {
  ADD_TO_PRESENTATION,
  isTruthy,
  isMozilla,
} from '@ardoq/common-helpers';
import {
  WithPopover,
  PlainTextPopover,
  WithUncontrolledPopover,
} from '@ardoq/popovers';
import { colors } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { connect } from '@ardoq/rxbeach';
import { getIsViewExportingStream } from '@ardoq/export/state';
import { ViewIds } from '@ardoq/api-types';
import { type I18nLocale, t } from '@ardoq/i18n';

const getOption = (
  label: string,
  onClick: MouseEventHandler
): DropdownItem => ({
  label,
  onClick,
  type: DropdownOptionType.OPTION,
});

type ExportButtonContentProps = {
  isExporting: boolean;
  isDisabled: boolean;
  dropdownOptions: DropdownItem[];
  exportLabel: string;
  disabledMessage: string;
};

const ExportButtonContent = ({
  isExporting,
  isDisabled,
  dropdownOptions,
  exportLabel,
  disabledMessage,
}: ExportButtonContentProps) =>
  isExporting ? (
    <GhostButton isLoading={true} />
  ) : (
    <WithUncontrolledPopover
      content={
        isDisabled ? (
          <PlainTextPopover>{disabledMessage}</PlainTextPopover>
        ) : null
      }
    >
      <FlexBox>
        <ButtonWithDropdown
          isDisabled={isDisabled}
          options={dropdownOptions}
          iconName={IconName.EXPORT}
          iconSize={IconSize.MEDIUM}
          tooltipText={isDisabled ? undefined : exportLabel}
          aria-label={exportLabel}
          data-click-id="export view button"
          buttonType={ButtonType.GHOST}
        />
      </FlexBox>
    </WithUncontrolledPopover>
  );

const ExportButton = ({
  viewId,
  exportToSvg,
  exportToPng,
  exportToLucidchart,
  addToPresentation,
  printOrSaveToPdf,
  isDisabled = false,
  disabledMessage,
  i18nLocale,
}: ExportConfigParams & { viewId: ViewIds; i18nLocale: I18nLocale }) => {
  const printOrSaveToPdfLabel = t(
    i18nLocale,
    'settingsBar:exportButton.printOrSaveToPdf',
    'Print or save to PDF'
  );
  const resolvedDisabledMessage =
    disabledMessage ??
    t(
      i18nLocale,
      'settingsBar:exportButton.viewEmpty',
      'This view is empty, there is nothing to export.'
    );
  const options: [string, MouseEventHandler | undefined][] = [
    [
      t(i18nLocale, 'settingsBar:exportButton.toSvg', 'Export to SVG'),
      exportToSvg,
    ],
    [
      t(i18nLocale, 'settingsBar:exportButton.toPng', 'Export to PNG'),
      exportToPng,
    ],
    [printOrSaveToPdfLabel, printOrSaveToPdf],
    [ADD_TO_PRESENTATION, addToPresentation],
  ];
  if (hasFeature(Features.LUCID_SCENARIO_EXPORT_ENABLE)) {
    options.push([
      t(
        i18nLocale,
        'settingsBar:exportButton.toLucidchart',
        'Export to Lucidchart'
      ),
      exportToLucidchart,
    ]);
  }
  const dropdownOptions: DropdownItem[] = options
    .map(([label, exporter]) => {
      if (!exporter) {
        return null;
      }

      if (label !== printOrSaveToPdfLabel || !isMozilla()) {
        return getOption(label, exporter);
      }

      const message = t(
        i18nLocale,
        'settingsBar:exportButton.firefoxUnsupported',
        'Due to a bug in Firefox this feature is currently not supported in this browser. Please try again in a different browser'
      );

      const disabledOptionWithPopover = (
        <WithPopover content={message}>
          <div style={{ color: colors.grey500 }}>{label}</div>
        </WithPopover>
      );

      return {
        label: disabledOptionWithPopover,
        type: DropdownOptionType.OPTION,
      };
    })
    .filter(isTruthy);

  const Content = useMemo(
    () => connect(ExportButtonContent, getIsViewExportingStream(viewId)),
    [viewId]
  );
  const exportLabel = t(i18nLocale, 'settingsBar:export', 'Export');
  return (
    <Content
      isDisabled={isDisabled}
      dropdownOptions={dropdownOptions}
      exportLabel={exportLabel}
      disabledMessage={resolvedDisabledMessage}
    />
  );
};

export { ExportButton };
